import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Space,
  Image,
  Card,
  Typography,
  Popconfirm,
  InputNumber,
  Grid
} from 'antd';
import {
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { API_ENDPOINTS } from './config';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const PostManager = () => {
  const [posts, setPosts] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const screens = useBreakpoint();

  // Fetch all posts
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_ENDPOINTS.LIST_POSTS);
      console.log(res.data);

      if (Array.isArray(res.data)) setPosts(res.data);
      else setPosts([]);
    } catch (err) {
      console.error('Error fetching posts:', err);
      message.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  // Open modal for add/edit
  const openModal = (edit = false, post = null) => {
    setIsEditing(edit);
    setCurrentPost(post);
    form.resetFields();
    if (post) {
      form.setFieldsValue(post);
      if (post.image_url) setFileList([{ url: post.image_url, name: 'Current Image', status: 'done' }]);
    } else {
      setFileList([]);
    }
    setModalVisible(true);
  };

  // Delete post
  const handleDelete = async (id) => {
    try {
      await axios.post(API_ENDPOINTS.DELETE_POST, { id });
      message.success('Post deleted successfully');
      fetchPosts();
    } catch (err) {
      console.error('Error deleting post:', err);
      message.error('Failed to delete post');
    }
  };


  // Add or edit post
  const handleFormSubmit = async (values) => {
    const formData = new FormData();
    Object.entries(values).forEach(([k, v]) => formData.append(k, v));
    if (isEditing && currentPost?.id) formData.append('id', currentPost.id);

    if (fileList.length > 0 && fileList[0].originFileObj) {
      formData.append('image_url', fileList[0].originFileObj);
    }

    try {
      setLoading(true);
      const res = await axios.post(API_ENDPOINTS.ADD_OR_EDIT_POST, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data?.error) {
        message.error(res.data.error);
      } else {
        message.success(res.data.message || 'Operation successful');
        // If adding new, set currentPost with new id for modal continuity
        if (!isEditing && res.data.id) {
          setCurrentPost({ ...values, id: res.data.id, image_url: res.data.image_url });
        }
        setModalVisible(false);
        fetchPosts();
      }
    } catch (err) {
      console.error('Operation failed:', err);
      message.error('Operation failed');
    } finally {
      setLoading(false);
    }
  };

  // Image upload validation
  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) message.error('You can only upload image files!');
    return isImage;
  };

  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  // Table columns
  const columns = [
    {
      title: 'Image',
      dataIndex: 'image_url',
      key: 'image_url',
      width: 100,
      responsive: ['md'],
      render: (image_url) =>
        image_url ? (
          <Image
            width={80}
            height={60}
            src={image_url}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            alt="Post thumbnail"
            preview={{ src: image_url }}
          />
        ) : (
          <div
            style={{
              width: 80,
              height: 60,
              backgroundColor: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 4,
            }}
          >
            <Text type="secondary">No Image</Text>
          </div>
        ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) =>
        text ? <Text ellipsis={{ tooltip: text }}>{text.substring(0, 50) + (text.length > 50 ? '...' : '')}</Text> :
          <Text type="secondary">Empty</Text>,
    },
        {
      title: 'Likes',
      dataIndex: 'like_count',
      key: 'like_count',
      render: (text) =>
        text ? <Text ellipsis={{ tooltip: text }}>{text.substring(0, 50) + (text.length > 50 ? '...' : '')}</Text> :
          <Text type="secondary">Empty</Text>,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      render: (value) => value != null ? `₹${parseFloat(value).toFixed(2)}` : <Text type="secondary">N/A</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: screens.md ? false : 'right',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openModal(true, record)} size="small" />
          <Popconfirm
            title="Are you sure to delete this post?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            placement="topRight"
          >
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: screens.xs ? 12 : 24 }}>
      <Card
        title={<Title level={4}>Post Management</Title>}
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openModal(false)}>Add New Post</Button>}
        bordered={false}
      >
        <Table
          dataSource={posts}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: true }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => `Total ${total} posts`,
            responsive: true
          }}
          size={screens.xs ? 'small' : 'middle'}
          sticky
        />
      </Card>

      <Modal
        title={isEditing ? 'Edit Post' : 'Add Post'}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
        width={screens.xs ? '90%' : 700}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={currentPost || { is_exists: 1 }}
          onFinish={handleFormSubmit}
        >
          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Please enter a description!' }]}
          >
            <Input.TextArea rows={4} placeholder="Enter post description" />
          </Form.Item>

          <Form.Item
            label="Price"
            name="price"
            rules={[{ required: true, message: 'Please enter a price!' }]}
          >
            <InputNumber placeholder="Enter price" min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="Post Image">
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={handleUploadChange}
              beforeUpload={beforeUpload}
              maxCount={1}
              accept="image/*"
            >
              {fileList.length >= 1 ? null : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
            {currentPost?.image_url && !fileList.length && (
              <Image
                width={100}
                src={currentPost.image_url}
                alt="Current post image"
                style={{ marginTop: 10, borderRadius: 4 }}
              />
            )}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>{isEditing ? 'Update' : 'Add'}</Button>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PostManager;
